import React from "react";
import FlexBetween from "components/FlexBetween";
import Header from "components/Header";
import {
  BusinessOutlined,
  Groups2Outlined,
  Search,
} from "@mui/icons-material";
import {
  Box,
  Button,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import BreakdownChart from "components/BreakdownChart";
import OverviewChart from "components/OverviewChart";
import { useGetDashboardQuery } from "state/api";
import StatBox from "components/StatBox";
import GridDash from "components/Dashboard/Grid";
import VerticalMarquee from "components/VerticalMarquee";

document.title = "AMLdiligence.com";

const Dashboard = () => {
  const navigate = useNavigate();
  const isNonMediumScreens = useMediaQuery("(min-width: 1200px)");

  const { data } = useGetDashboardQuery(undefined, {
    pollingInterval: 10800000,
    refetchOnFocus: true,
    refetchOnReconnect: true,
  });

  const handleInstantScreeningClick = () => {
    navigate("/search");
  };

  return (
    <Box sx={{ p: { xs: "1rem", md: "2rem 2.5rem" }, fontFamily: "IBM Plex Sans, sans-serif" }}>
      <FlexBetween>
        <Header title="DASHBOARD" subtitle="Welcome to your dashboard" />
        <Button
          onClick={handleInstantScreeningClick}
          endIcon={<Search sx={{ fontSize: 16 }} />}
          sx={{
            px: 3,
            py: 1.25,
            borderRadius: "10px",
            fontSize: 13,
            fontWeight: 600,
            textTransform: "none",
            fontFamily: "IBM Plex Sans, sans-serif",
            background: "linear-gradient(135deg,#D97706,#F59E0B)",
            color: "#fff",
            boxShadow: "0 4px 14px rgba(217,119,6,.3)",
            "&:hover": { background: "linear-gradient(135deg,#B45309,#D97706)" },
          }}
        >
          Instant Screen ({data?.screened})
        </Button>
      </FlexBetween>

      <Box
        mt="20px"
        display="grid"
        gridTemplateColumns="repeat(12, 1fr)"
        gridAutoRows="160px"
        gap="20px"
        sx={{
          "& > div": { gridColumn: isNonMediumScreens ? undefined : "span 12" },
        }}
      >
        {/* ROW 1 */}
        <StatBox
          title="Customers"
          value={data && data.individuals}
          increase={data && data.expiredIndividuals}
          description="Expired ID Docs"
          icon={<Groups2Outlined sx={{ color: "#D97706", fontSize: "20px" }} />}
        />
        <StatBox
          title="Entities"
          value={data && data.entities}
          increase={data && data.expiredEntities}
          description="Expired Licenses"
          icon={<BusinessOutlined sx={{ color: "#D97706", fontSize: "20px" }} />}
        />

        {/* Overview Chart */}
        <Box
          gridColumn="span 4"
          gridRow="span 2"
          sx={{
            background: "#fff",
            border: "1px solid #E2E8F0",
            borderRadius: "12px",
            p: "1.5rem",
          }}
        >
          <OverviewChart view="sales" isDashboard={true} />
        </Box>

        <VerticalMarquee />

        {/* Case Management */}
        <Box
          gridColumn="span 2"
          gridRow="span 1"
          sx={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
            p: "1.25rem 1rem",
            background: "#fff",
            border: "1px solid #E2E8F0",
            borderRadius: "12px",
          }}
        >
          <FlexBetween>
            <Typography
              sx={{
                fontSize: 13,
                fontWeight: 700,
                color: "#0F172A",
                fontFamily: "IBM Plex Sans, sans-serif",
              }}
            >
              Case Management
            </Typography>
          </FlexBetween>
          <FlexBetween>
            <Typography sx={{ fontSize: 13, color: "#64748B", fontFamily: "IBM Plex Sans, sans-serif" }}>
              Identified
            </Typography>
            <Typography sx={{ fontSize: 13, fontWeight: 600, color: "#0F172A", fontFamily: "IBM Plex Sans, sans-serif" }}>
              {data?.identified}
            </Typography>
          </FlexBetween>
          <FlexBetween>
            <Typography sx={{ fontSize: 13, color: "#64748B", fontFamily: "IBM Plex Sans, sans-serif" }}>
              Cleared
            </Typography>
            <Typography sx={{ fontSize: 13, fontWeight: 600, color: "#0F172A", fontFamily: "IBM Plex Sans, sans-serif" }}>
              {data?.cleared}
            </Typography>
          </FlexBetween>
          <FlexBetween>
            <Typography sx={{ fontSize: 13, color: "#64748B", fontFamily: "IBM Plex Sans, sans-serif" }}>
              Pending
            </Typography>
            <Typography sx={{ fontSize: 13, fontWeight: 600, color: "#0F172A", fontFamily: "IBM Plex Sans, sans-serif" }}>
              {data?.pending}
            </Typography>
          </FlexBetween>
        </Box>

        {/* Screening */}
        <Box
          gridColumn="span 2"
          gridRow="span 1"
          sx={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
            p: "1.25rem 1rem",
            background: "#fff",
            border: "1px solid #E2E8F0",
            borderRadius: "12px",
          }}
        >
          <FlexBetween>
            <Typography sx={{ fontSize: 13, fontWeight: 700, color: "#0F172A", fontFamily: "IBM Plex Sans, sans-serif" }}>
              Screening
            </Typography>
            <Typography sx={{ fontSize: 13, fontWeight: 600, color: "#0F172A", fontFamily: "IBM Plex Sans, sans-serif" }}>
              {data?.screened}
            </Typography>
          </FlexBetween>
          <FlexBetween>
            <Typography sx={{ fontSize: 13, color: "#64748B", fontFamily: "IBM Plex Sans, sans-serif" }}>
              PEP Status
            </Typography>
            <Typography sx={{ fontSize: 13, fontWeight: 600, color: "#0F172A", fontFamily: "IBM Plex Sans, sans-serif" }}>
              {data?.pep}
            </Typography>
          </FlexBetween>
          <FlexBetween>
            <Typography sx={{ fontSize: 13, color: "#64748B", fontFamily: "IBM Plex Sans, sans-serif" }}>
              PNMR
            </Typography>
            <Typography sx={{ fontSize: 13, fontWeight: 600, color: "#0F172A", fontFamily: "IBM Plex Sans, sans-serif" }}>
              {data?.pnmr}
            </Typography>
          </FlexBetween>
          <FlexBetween>
            <Typography sx={{ fontSize: 13, color: "#64748B", fontFamily: "IBM Plex Sans, sans-serif" }}>
              FFR
            </Typography>
            <Typography sx={{ fontSize: 13, fontWeight: 600, color: "#0F172A", fontFamily: "IBM Plex Sans, sans-serif" }}>
              {data?.ffr}
            </Typography>
          </FlexBetween>
          <FlexBetween>
            <Typography sx={{ fontSize: 13, color: "#64748B", fontFamily: "IBM Plex Sans, sans-serif" }}>
              System cleared
            </Typography>
            <Typography sx={{ fontSize: 13, fontWeight: 600, color: "#0F172A", fontFamily: "IBM Plex Sans, sans-serif" }}>
              {data?.systemClear + data?.cleared}
            </Typography>
          </FlexBetween>
        </Box>

        {/* ROW 2 */}
        <Box
          gridColumn="span 8"
          gridRow="span 2"
          sx={{
            background: "#fff",
            border: "1px solid #E2E8F0",
            borderRadius: "12px",
            "& .MuiDataGrid-root": { border: "none" },
            "& .MuiDataGrid-cell": { borderBottom: "none" },
            "& .MuiDataGrid-columnHeaders": {
              background: "#F8FAFC",
              color: "#0F172A",
              borderBottom: "1px solid #E2E8F0",
            },
            "& .MuiDataGrid-virtualScroller": { background: "#fff" },
            "& .MuiDataGrid-footerContainer": {
              background: "#F8FAFC",
              borderTop: "1px solid #E2E8F0",
            },
          }}
        >
          <GridDash />
        </Box>

        <Box
          gridColumn="span 4"
          gridRow="span 2"
          sx={{
            background: "#fff",
            border: "1px solid #E2E8F0",
            borderRadius: "12px",
            p: "1.5rem",
          }}
        >
          <Typography
            sx={{
              fontSize: 15,
              fontWeight: 700,
              color: "#0F172A",
              fontFamily: "IBM Plex Sans, sans-serif",
              mb: 1,
            }}
          >
            Useful Links
          </Typography>
          <BreakdownChart isDashboard={true} />
        </Box>
      </Box>
    </Box>
  );
};

export default Dashboard;
